/* eslint-disable complexity */
import { ParameterList } from 'src/components/molecules';
import { RoutingDataSource, SUBMISSION_MODE } from 'src/types';
import { isRulePropsPopulated } from './isRulePropsPopulated';
import { Fact } from './RoutingRule.types';

enum Operator {
  In = 'in',
  ArrayIn = 'ArrayIn',
}

export const writeRoutingRule = (
  rule: ParameterList,
  dataSource: RoutingDataSource,
  featureFlags?: { isRelationshipToOrgEnabled?: boolean },
) => {
  const reportParameters = [
    'submissionMode',
    'anonymous',
    'reportedCategory',
    'coiCategory',
    'questionnaireId',
  ];

  if (featureFlags && featureFlags.isRelationshipToOrgEnabled) {
    reportParameters.push('relationshipToCompany');
  }

  if (!isRulePropsPopulated(rule)) {
    return {};
  }

  const all = [];
  const hasNamedOffender = dataSource === RoutingDataSource.OFFENDER;
  const userBaseRule = {
    fact: hasNamedOffender ? 'offenders' : 'reporter',
    operator: hasNamedOffender ? Operator.ArrayIn : Operator.In,
  } as { fact: Fact; operator: Operator };
  const reportBaseRule = {
    fact: 'report',
    operator: Operator.In,
  } as { fact: Fact; operator: Operator };

  if (rule.site) {
    const baseRule = reportParameters.includes('site')
      ? reportBaseRule
      : userBaseRule;
    all.push({
      ...baseRule,
      value: rule.site,
      path: `$.site`,
    });
  }

  if (rule.department) {
    const baseRule = reportParameters.includes('department')
      ? reportBaseRule
      : userBaseRule;
    all.push({
      ...baseRule,
      value: rule.department,
      path: `$.department`,
    });
  }

  if (rule.country) {
    const baseRule = reportParameters.includes('country')
      ? reportBaseRule
      : userBaseRule;
    all.push({
      ...baseRule,
      value: rule.country,
      path: `$.country`,
    });
  }

  if (rule.anonymous) {
    const baseRule = reportParameters.includes('anonymous')
      ? reportBaseRule
      : userBaseRule;
    all.push({
      ...baseRule,
      value: rule.anonymous,
      path: `$.anonymous`,
    });
  }

  if (rule.reportedCategory) {
    const baseRule = reportParameters.includes('reportedCategory')
      ? reportBaseRule
      : userBaseRule;
    all.push({
      ...baseRule,
      value: rule.reportedCategory,
      path: `$.reportedCategory`,
    });
  }

  if (rule.relationshipToCompany) {
    const baseRule = reportParameters.includes('relationshipToCompany')
      ? reportBaseRule
      : userBaseRule;
    all.push({
      ...baseRule,
      value: rule.relationshipToCompany,
      path: `$.relationshipToCompany`,
    });
  }

  if (rule.systemRoles) {
    const baseRule = reportParameters.includes('systemRoles')
      ? reportBaseRule
      : userBaseRule;
    all.push({
      ...baseRule,
      value: rule.systemRoles,
      path: `$.systemRoles`,
      operator: Operator.ArrayIn,
    });
  }

  if (rule.submissionMode) {
    const baseRule = reportParameters.includes('submissionMode')
      ? reportBaseRule
      : userBaseRule;
    const mobileSelected = rule.submissionMode.includes(
      SUBMISSION_MODE.APP_GO_TOGETHER,
    );
    const slackSelected = rule.submissionMode.includes(
      SUBMISSION_MODE.SLACK_GO_AHEAD,
    );

    // if the user has selected Mobile App, include all mobile intake methods (1, 2, 3)
    // if the user has selected Slack, include all slack intake methods (7, 8)
    // but make sure not to double include them
    let selectedSubmissionModes = rule.submissionMode;
    if (mobileSelected) {
      selectedSubmissionModes = [
        ...new Set([
          ...rule.submissionMode,
          SUBMISSION_MODE.APP_GO_AHEAD,
          SUBMISSION_MODE.APP_GO_ANONYMOUS,
        ]),
      ];
    }

    if (slackSelected) {
      selectedSubmissionModes = [
        ...new Set([
          ...selectedSubmissionModes,
          SUBMISSION_MODE.SLACK_GO_ANONYMOUS,
        ]),
      ];
    }

    const reporterPortalSelected = rule.submissionMode.includes(
      SUBMISSION_MODE.REPORTER_PORTAL,
    );

    if (reporterPortalSelected) {
      selectedSubmissionModes = [
        ...new Set([
          ...selectedSubmissionModes,
          SUBMISSION_MODE.REPORTER_PORTAL_GO_TOGETHER,
        ]),
      ];
    }

    all.push({
      ...baseRule,
      value: selectedSubmissionModes,
      path: `$.submissionMode`,
    });
  }

  if (rule['userMetaData.division']) {
    const baseRule = reportParameters.includes('userMetaData.division')
      ? reportBaseRule
      : userBaseRule;
    all.push({
      ...baseRule,
      value: rule['userMetaData.division'],
      path: `$.userMetaData.division`,
    });
  }

  if (rule['userMetaData.organization']) {
    const baseRule = reportParameters.includes('userMetaData.organization')
      ? reportBaseRule
      : userBaseRule;
    all.push({
      ...baseRule,
      value: rule['userMetaData.organization'],
      path: `$.userMetaData.organization`,
    });
  }

  if (rule['Call.organisationHotlineNumber']) {
    all.push({
      ...reportBaseRule,
      value: rule['Call.organisationHotlineNumber'],
      path: `$.Call.organisationHotlineNumber`,
    });
  }

  if (rule.coiCategory) {
    all.push({
      ...reportBaseRule,
      value: rule.coiCategory,
      path: `$.coiCategory`,
    });
  }

  if (rule.questionnaireId) {
    all.push({
      ...reportBaseRule,
      value: rule.questionnaireId,
      path: `$.questionnaireId`,
    });
  }

  return {
    all,
  };
};
